import { dinero, toSnapshot } from 'dinero.js'
import { getDineroCurrency, DEFAULT_LOCALE } from './constants'
import type { FormatCurrencyOptions } from './types'

/**
 * Format a monetary amount into a locale-aware display string.
 *
 * Uses dinero.js for safe integer conversion and Intl.NumberFormat for display.
 *
 * @example
 * formatCurrency({ amount: 1250, currencyCode: 'USD' })
 * // => "$12.50"
 *
 * formatCurrency({ amount: 1200, currencyCode: 'CAD', compact: true })
 * // => "CA$12"
 *
 * formatCurrency({ amount: 1250, currencyCode: 'USD', showCode: true })
 * // => "USD 12.50"
 */
export function formatCurrency({
  amount,
  currencyCode,
  locale,
  compact = false,
  showCode = false,
}: FormatCurrencyOptions): string {
  const code = currencyCode.toUpperCase()
  const currency = getDineroCurrency(code)
  const d = dinero({ amount, currency })
  const snapshot = toSnapshot(d)

  // Convert minor units to major units (e.g. 1250 cents -> 12.50)
  const base = Array.isArray(snapshot.currency.base)
    ? snapshot.currency.base[0]
    : snapshot.currency.base
  const exponent = snapshot.currency.exponent
  const decimalValue = snapshot.amount / Math.pow(base, exponent)

  const resolvedLocale = locale ?? DEFAULT_LOCALE[code] ?? 'en-US'

  const isWhole = decimalValue === Math.floor(decimalValue)

  const formatOptions: Intl.NumberFormatOptions = {
    style: showCode ? 'decimal' : 'currency',
    currency: showCode ? undefined : code,
    minimumFractionDigits: compact && isWhole ? 0 : exponent,
    maximumFractionDigits: exponent,
  }

  try {
    const formatted = new Intl.NumberFormat(
      resolvedLocale,
      formatOptions
    ).format(decimalValue)

    return showCode ? `${code} ${formatted}` : formatted
  } catch {
    // Graceful fallback for unsupported currencies/locales
    return `${code} ${decimalValue.toFixed(exponent)}`
  }
}
